import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Textarea,
  Select,
  HStack,
  VStack,
  Text,
  useToast,
  FormControl,
  FormLabel,
  Spinner,
  Box,
  Divider,
} from '@chakra-ui/react';
import { FiPlay, FiSquare, FiSave } from 'react-icons/fi';
import { TenantSettingsAPI } from '../../services/api';

function formatServerErrorPayload(parsed) {
  if (parsed == null) return 'Unknown error';
  if (typeof parsed === 'string') return parsed;
  const d = parsed.detail;
  if (d != null) {
    if (typeof d === 'string') return d;
    if (typeof d === 'object' && d.message != null) return String(d.message);
    try {
      return JSON.stringify(d);
    } catch {
      return String(d);
    }
  }
  if (parsed.message != null) return String(parsed.message);
  if (parsed.error != null) return String(parsed.error);
  try {
    return JSON.stringify(parsed);
  } catch {
    return 'Unknown error';
  }
}

/** Axios errors for responseType 'blob' put JSON bodies in a Blob — parse for readable toasts. */
async function messageFromVoiceTestError(error) {
  if (!error.response) {
    return error.message || 'Network error. Check your connection and try again.';
  }
  const { data, status } = error.response;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      if (!text?.trim()) {
        if (status === 503) {
          return 'Voice service is unavailable. If this persists, the server may not be able to reach the voice engine.';
        }
        if (status === 504) {
          return 'Voice test timed out. Try again with shorter text.';
        }
        return `Request failed (${status})`;
      }
      const trimmed = text.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          return formatServerErrorPayload(JSON.parse(trimmed));
        } catch {
          return trimmed.slice(0, 400);
        }
      }
      return trimmed.slice(0, 400);
    } catch {
      return `Request failed (${status})`;
    }
  }

  if (typeof data === 'string') {
    try {
      return formatServerErrorPayload(JSON.parse(data));
    } catch {
      return data.slice(0, 400);
    }
  }

  if (data && typeof data === 'object') {
    return formatServerErrorPayload(data);
  }

  return error.message || `Request failed (${status})`;
}

function isRiffWavPrefix(buffer) {
  if (!buffer || buffer.byteLength < 4) return false;
  const u8 = new Uint8Array(buffer);
  return u8[0] === 0x52 && u8[1] === 0x49 && u8[2] === 0x46 && u8[3] === 0x46;
}

export const VoiceTestPlayground = ({
  isOpen,
  onClose,
  voiceOptions,
  initialVoice,
  initialText,
  onSave,
  tenantId,
}) => {
  const toast = useToast();
  const [text, setText] = useState(initialText || '');
  const [voice, setVoice] = useState(initialVoice || 'Aoede');
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState(null);

  useEffect(() => {
    setText(initialText || '');
  }, [initialText]);

  useEffect(() => {
    setVoice(initialVoice || 'Aoede');
  }, [initialVoice]);

  const playVoice = async () => {
    if (!text.trim()) {
      toast({
        title: 'Please enter text to speak',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    if (!tenantId || !String(tenantId).trim()) {
      toast({
        title: 'Cannot test voice',
        description: 'Tenant context is missing. Reload the page or sign in again.',
        status: 'warning',
        duration: 4000,
      });
      return;
    }

    setIsPlaying(true);

    try {
      const response = await TenantSettingsAPI.testGeminiLiveVoice(tenantId, {
        text: text.trim(),
        voice_name: voice,
      });

      const raw = response.data instanceof Blob ? response.data : new Blob([response.data]);
      const ab = await raw.arrayBuffer();

      if (!isRiffWavPrefix(ab)) {
        setIsPlaying(false);
        let description = 'The server did not return audio.';
        try {
          const t = new TextDecoder().decode(ab).trim();
          if (t.startsWith('{') || t.startsWith('[')) {
            description = formatServerErrorPayload(JSON.parse(t));
          } else if (t) {
            description = t.slice(0, 400);
          }
        } catch {
          /* keep default */
        }
        toast({
          title: 'Failed to generate voice',
          description,
          status: 'error',
          duration: 5000,
        });
        return;
      }

      const audioBlob = new Blob([ab], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      audio.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
        toast({
          title: 'Failed to play audio',
          description: 'The file may be corrupt or unsupported in this browser.',
          status: 'error',
          duration: 3000,
        });
      };

      try {
        await audio.play();
      } catch (playErr) {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
        toast({
          title: 'Could not start playback',
          description: playErr?.message || 'Your browser may have blocked audio. Try again or check site permissions.',
          status: 'warning',
          duration: 4000,
        });
        return;
      }

      setAudioElement(audio);

      toast({
        title: 'Playing AI voice',
        description: `Voice: ${voice}`,
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Voice test error:', error);
      setIsPlaying(false);

      const errorMsg = await messageFromVoiceTestError(error);
      toast({
        title: 'Failed to generate voice',
        description: errorMsg,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const stopVoice = () => {
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
      setAudioElement(null);
    }
    setIsPlaying(false);
  };

  const handleSave = () => {
    onSave(voice, text);
    onClose();
  };

  const handleClose = () => {
    stopVoice();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>AI Voice Playground</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={5} align="stretch">
            <Text fontSize="sm" color="gray.600">
              Preview the production voice (Gemini TTS). Save AI Settings on the main page to persist
              voice and welcome message.
            </Text>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Voice
              </FormLabel>
              <Select
                value={voice}
                onChange={(e) => setVoice(e.target.value)}
                isDisabled={isPlaying}
              >
                {voiceOptions.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Text to Speak
              </FormLabel>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter text to speak with AI voice..."
                rows={5}
                isDisabled={isPlaying}
                maxLength={500}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                {text.length}/500 characters
              </Text>
            </FormControl>

            <Divider />

            <HStack spacing={3}>
              {!isPlaying ? (
                <Button
                  leftIcon={<FiPlay />}
                  onClick={playVoice}
                  colorScheme="blue"
                  isDisabled={!text.trim()}
                  flex={1}
                >
                  Play Voice
                </Button>
              ) : (
                <Button
                  leftIcon={<FiSquare />}
                  onClick={stopVoice}
                  colorScheme="red"
                  flex={1}
                >
                  Stop
                </Button>
              )}
            </HStack>

            {isPlaying && (
              <Box textAlign="center" py={2}>
                <Spinner size="sm" mr={2} />
                <Text fontSize="sm" color="gray.600" display="inline">
                  Generating audio...
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={isPlaying}>
            Cancel
          </Button>
          <Button
            leftIcon={<FiSave />}
            colorScheme="green"
            onClick={handleSave}
            isDisabled={isPlaying}
          >
            Save Settings
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
